import { auditLogService } from './auditLog';
import { getCurrentUserId } from '@utils/userUtils';
import { getSelectedFacilityFromSession } from '@utils/facilityUtils';
import { authenticatedFetch } from '@utils/authenticatedFetch';
import { isIdleTimedOut, markUserActivity, setSessionIdleTimeoutFromExpiresIn, clearSessionIdleTimeout } from '@utils/sessionActivity';
import { getApiOrigin, getIdentityOrigin } from '@utils/apiOrigin';
import {
  HUB_IDENTITY_AUDIENCE,
  IDENTITY_AUTH_PATHS,
  RULES_DOMAIN_CONTEXT_PATH,
} from '@constants/identityLogin';
import { invalidateMyFacilitiesCache } from '@services/facilityServices';
import {
  WORKING_ORGANIZATION_NAME_KEY,
  persistWorkingOrganizationName,
  readAccessTokenOrgClaims,
  readPersistedWorkingOrganizationName,
} from '@utils/workingOrganizationName';

const RULES_BASE_URL = getApiOrigin();
const LOGIN_PATH = IDENTITY_AUTH_PATHS.login;
const OIDC_START_PATH = IDENTITY_AUTH_PATHS.oidcStart;
const OIDC_CALLBACK_PATH = IDENTITY_AUTH_PATHS.oidcCallback;
const UNICOMPLY_SIGNUP_OIDC_START_PATH = IDENTITY_AUTH_PATHS.unicomplySignupOidcStart;
const DOMAIN_CONTEXT_PATH = RULES_DOMAIN_CONTEXT_PATH;
const IDENTITY_AUDIENCE = HUB_IDENTITY_AUDIENCE;
const REGISTER_PATH = "/api/v1/auth/register";
const OTP_SEND_PATH = "/api/v1/otp/send";
const OTP_VERIFY_PATH = "/api/v1/otp/verify";
const RESET_PASSWORD_PATH = "/api/v1/auth/reset-password";
const LOGIN_FETCH_NETWORK_USER_MESSAGE =
  "Failed to Fetch Licence from Licence Server.";
function loginFetchNetworkUserMessage(): string {
  const origin = getIdentityOrigin() || "Identity";
  return `Failed to reach Identity server (${origin}). Check network connectivity and REACT_APP_IDENTITY_BASE_URL.`;
}

type AuthAuditMethod = "MSAL" | "Google" | "Email" | "OTP";

function requireIdentityBaseUrl(): string {
  const origin = getIdentityOrigin();
  if (!origin) {
    throw new Error(
      "REACT_APP_IDENTITY_BASE_URL (or REACT_APP_MCM_URL) is not set.",
    );
  }
  return origin;
}

function requireRulesBaseUrl(): string {
  if (!RULES_BASE_URL) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }
  return RULES_BASE_URL;
}

export interface UserPreferredPlan {
  user_id: number;
  plan_tier_id: number;
  plan_name: string;
}

export interface Permission {
  feature_id: number;
  feature_name: string;
  view: boolean;
  add: boolean;
  update: boolean;
  delete: boolean;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: {
    user_preferred_plan?: UserPreferredPlan;
    is_verified?: boolean;
    [key: string]: unknown;
  };
  permissions?: Permission[];
}

export type OidcProvider = "google" | "microsoft";

export const ACCOUNT_STATE_NO_ORG = "no_org";
export const ACCOUNT_STATE_STORAGE_KEY = "account_state";

type IdentityTokenPayload = {
  status?: string;
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  refresh_token?: string;
  detail?: string;
  message?: string;
  is_verified?: boolean;
  challenge_token?: string;
  selection_challenge?: string;
  organizations?: unknown[];
  organization_id?: string | null;
  account_state?: string | null;
};

type DomainContextPayload = {
  user: LoginResponse["user"];
  permissions?: Permission[];
  user_preferred_plan?: UserPreferredPlan;
};

/** Display-only identity JWT claims (no signature verify). */
export type IdentityProfileClaims = {
  email?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
};

const NO_ORG_ONBOARDING_USER_FIELDS = [
  "id",
  "user_id",
  "username",
  "first_name",
  "last_name",
  "display_name",
  "phone_number",
  "is_verified",
  "can_sales_bypass",
  "can_use_network_facility",
] as const;

function asTrimmedString(value: unknown): string {
  if (value == null) return "";
  const s = String(value).trim();
  return s;
}

/** Decode access-token payload for Hub profile hydrate (SSO no-org). */
export function readIdentityProfileClaims(
  accessToken: string | null | undefined,
): IdentityProfileClaims {
  const token = String(accessToken ?? "").trim();
  if (!token || token.split(".").length < 2) return {};
  try {
    const payloadPart = token.split(".")[1];
    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const pad =
      normalized.length % 4 === 0 ? "" : "=".repeat(4 - (normalized.length % 4));
    const json = atob(normalized + pad);
    const claims = JSON.parse(json) as Record<string, unknown>;
    const email = asTrimmedString(claims.email).toLowerCase() || undefined;
    const given = asTrimmedString(claims.given_name);
    const family = asTrimmedString(claims.family_name);
    const display =
      asTrimmedString(claims.display_name) ||
      asTrimmedString(claims.name) ||
      (given && family ? `${given} ${family}` : given || family) ||
      undefined;
    return {
      email,
      display_name: display,
      first_name: given || undefined,
      last_name: family || undefined,
    };
  } catch {
    return {};
  }
}

function emailLocalPart(email: string): string {
  const normalized = email.trim().toLowerCase();
  const at = normalized.indexOf("@");
  return at >= 0 ? normalized.slice(0, at) : normalized;
}

/** Apply identity JWT name claims onto a Hub user record. */
export function mergeIdentityProfileIntoUser(
  user: Record<string, unknown>,
  profileClaims: IdentityProfileClaims,
  email: string,
): Record<string, unknown> {
  const merged = { ...user };
  const resolvedEmail =
    (typeof merged.email === "string" && merged.email.trim()
      ? merged.email.trim()
      : "") ||
    email.trim() ||
    profileClaims.email ||
    "";
  const emailLocal = emailLocalPart(resolvedEmail);

  if (
    profileClaims.first_name &&
    (!merged.first_name || String(merged.first_name).trim() === emailLocal)
  ) {
    merged.first_name = profileClaims.first_name;
  } else if (!merged.first_name && profileClaims.first_name) {
    merged.first_name = profileClaims.first_name;
  }

  if (!merged.last_name && profileClaims.last_name) {
    merged.last_name = profileClaims.last_name;
  }
  if (!merged.display_name && profileClaims.display_name) {
    merged.display_name = profileClaims.display_name;
  }
  if (!merged.display_name) {
    const first = asTrimmedString(merged.first_name);
    const last = asTrimmedString(merged.last_name);
    if (first || last) {
      merged.display_name = [first, last].filter(Boolean).join(" ");
    } else if (resolvedEmail) {
      merged.display_name = emailLocalPart(resolvedEmail);
    }
  }
  return merged;
}

function buildNoOrgOnboardingUser(
  domainContext: DomainContextPayload | null,
  email: string,
  profileClaims: IdentityProfileClaims = {},
): LoginResponse["user"] {
  const contextUser = domainContext?.user as Record<string, unknown> | undefined;
  const resolvedEmail =
    (typeof contextUser?.email === "string" && contextUser.email.trim()
      ? contextUser.email.trim()
      : "") ||
    email.trim() ||
    profileClaims.email ||
    "";
  const user: Record<string, unknown> = {
    email: resolvedEmail,
    account_state: ACCOUNT_STATE_NO_ORG,
  };

  for (const field of NO_ORG_ONBOARDING_USER_FIELDS) {
    const value = contextUser?.[field];
    if (value !== undefined && value !== null) {
      user[field] = value;
    }
  }

  return mergeIdentityProfileIntoUser(user, profileClaims, resolvedEmail) as LoginResponse["user"];
}

function loginRedirectUri(): string {
  return `${window.location.origin}/login`;
}

async function loadDomainContext(accessToken: string): Promise<DomainContextPayload> {
  const response = await fetch(`${requireRulesBaseUrl()}${DOMAIN_CONTEXT_PATH}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      JSON.stringify({
        detail: text || `Failed to load account context (${response.status})`,
        status: response.status,
      }),
    );
  }
  return (await response.json()) as DomainContextPayload;
}

function isExplicitNoOrgToken(tokenPayload: IdentityTokenPayload): boolean {
  return (
    tokenPayload.account_state === ACCOUNT_STATE_NO_ORG &&
    (tokenPayload.organization_id == null || tokenPayload.organization_id === "")
  );
}

export function isNoOrgAccountSession(): boolean {
  return localStorage.getItem(ACCOUNT_STATE_STORAGE_KEY) === ACCOUNT_STATE_NO_ORG;
}

export type OnboardingStatus = {
  account_state?: string;
  memberships?: Array<{ org_id?: number | string }>;
  organization_id?: number | string | null;
  error?: string;
};

/** Poll MCM after purchase/admin grant. Does not grant membership from the browser. */
export async function fetchOnboardingStatus(): Promise<OnboardingStatus> {
  const token = localStorage.getItem("access_token");
  if (!token) {
    throw new Error("Not authenticated");
  }
  const response = await fetch(
    `${requireIdentityBaseUrl()}${IDENTITY_AUTH_PATHS.onboardingStatus}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    },
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Onboarding status failed (${response.status})`);
  }
  return (await response.json()) as OnboardingStatus;
}

/**
 * After membership attach, clear no-org marker and force re-login so Hub
 * loads an org-scoped token + domain-context. Access-token-only Hub has no
 * silent refresh path yet.
 */
export async function completeNoOrgOnboardingAfterMembership(): Promise<void> {
  localStorage.removeItem(ACCOUNT_STATE_STORAGE_KEY);
  await logoutLocal();
}

function clearPartialAuthState(): void {
  localStorage.removeItem("access_token");
  localStorage.removeItem("access_token_expires_at");
  localStorage.removeItem("user");
  localStorage.removeItem("user_permissions");
  localStorage.removeItem(ACCOUNT_STATE_STORAGE_KEY);
  localStorage.removeItem(WORKING_ORGANIZATION_NAME_KEY);
  sessionStorage.removeItem("selected_plan");
  clearSessionIdleTimeout();
  invalidateMyFacilitiesCache();
}

async function persistIdentitySession(
  tokenPayload: IdentityTokenPayload,
  email: string,
): Promise<LoginResponse> {
  const accessToken = tokenPayload.access_token;
  if (!accessToken) {
    throw new Error(JSON.stringify({ detail: "Login response missing access token." }));
  }

  const profileClaims = readIdentityProfileClaims(accessToken);
  const resolvedEmail = email.trim() || profileClaims.email || "";
  const expiresIn = tokenPayload.expires_in ?? 3600;
  const noOrg = isExplicitNoOrgToken(tokenPayload);

  // Validate remote context before committing member sessions. For no-org
  // onboarding, load context best-effort so facility setup has the Rules user
  // id required before checkout creates the organization.
  let domainContext: DomainContextPayload | null = null;
  if (noOrg) {
    try {
      domainContext = await loadDomainContext(accessToken);
    } catch {
      // A newly-created identity may not be provisioned in Rules yet. Preserve
      // no-org login; facility setup remains unavailable until provisioning.
      domainContext = null;
    }
  } else {
    try {
      domainContext = await loadDomainContext(accessToken);
    } catch (error) {
      clearPartialAuthState();
      throw error;
    }
  }

  try {
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("access_token_expires_at", String(Date.now() + expiresIn * 1000));
    setSessionIdleTimeoutFromExpiresIn(expiresIn);
    markUserActivity();
    invalidateMyFacilitiesCache();

    if (noOrg) {
      const user = buildNoOrgOnboardingUser(
        domainContext,
        resolvedEmail,
        profileClaims,
      );
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem(ACCOUNT_STATE_STORAGE_KEY, ACCOUNT_STATE_NO_ORG);
      // Keep domain-context permissions for no-org facility setup (Add Facility).
      const noOrgPermissions = domainContext?.permissions;
      if (Array.isArray(noOrgPermissions) && noOrgPermissions.length > 0) {
        localStorage.setItem("user_permissions", JSON.stringify(noOrgPermissions));
      } else {
        localStorage.removeItem("user_permissions");
      }
      sessionStorage.removeItem("selected_plan");
      auditLogService.refreshSession();
      await auditLogService.logAuthentication("login", "Email", {
        success: true,
        email: resolvedEmail,
        reason: "no_org",
      });
      return {
        access_token: accessToken,
        token_type: tokenPayload.token_type ?? "bearer",
        expires_in: expiresIn,
        user,
        permissions: Array.isArray(noOrgPermissions) ? noOrgPermissions : [],
      };
    }

    localStorage.removeItem(ACCOUNT_STATE_STORAGE_KEY);
    const user = mergeIdentityProfileIntoUser(
      {
        ...(domainContext?.user ?? { email: resolvedEmail }),
      } as Record<string, unknown>,
      profileClaims,
      resolvedEmail,
    );
    // Keep working-org label across domain-context refresh when already known.
    const orgName =
      readPersistedWorkingOrganizationName() ||
      String(user.organization_name ?? user.org_name ?? "").trim() ||
      readAccessTokenOrgClaims(accessToken);
    if (orgName) {
      user.organization_name = orgName;
      persistWorkingOrganizationName(orgName);
    }
    localStorage.setItem("user", JSON.stringify(user));

    const permissions = domainContext?.permissions;
    if (permissions && Array.isArray(permissions)) {
      localStorage.setItem("user_permissions", JSON.stringify(permissions));
    } else {
      const userPermissions = (user as { permissions?: Permission[] }).permissions;
      if (userPermissions && Array.isArray(userPermissions)) {
        localStorage.setItem("user_permissions", JSON.stringify(userPermissions));
      }
    }

    const preferredPlan =
      domainContext?.user_preferred_plan ??
      (user as { user_preferred_plan?: UserPreferredPlan }).user_preferred_plan;
    if (preferredPlan) {
      sessionStorage.setItem(
        "selected_plan",
        JSON.stringify({
          id: preferredPlan.plan_tier_id,
          title: preferredPlan.plan_name,
        }),
      );
    }

    auditLogService.refreshSession();
    await auditLogService.logAuthentication("login", "Email", {
      success: true,
      email: resolvedEmail,
    });

    return {
      access_token: accessToken,
      token_type: tokenPayload.token_type ?? "bearer",
      expires_in: expiresIn,
      user,
      permissions: permissions ?? (user as { permissions?: Permission[] }).permissions,
    };
  } catch (error) {
    clearPartialAuthState();
    throw error;
  }
}

async function postIdentityAuth(
  path: string,
  body: Record<string, unknown>,
  email: string,
  failureLabel: AuthAuditMethod,
): Promise<LoginResponse> {
  const identityOrigin = requireIdentityBaseUrl();
  let response: Response;
  try {
    response = await fetch(`${identityOrigin}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (fetchError: unknown) {
    const rawMessage = String((fetchError as Error)?.message ?? "").trim();
    const isGenericFetchFailure =
      !rawMessage ||
      /^failed to fetch$/i.test(rawMessage) ||
      /^network error:\s*failed to fetch$/i.test(rawMessage);
    const userFacingDetail = isGenericFetchFailure
      ? loginFetchNetworkUserMessage()
      : rawMessage;
    await auditLogService.logAuthentication("login_failed", failureLabel, {
      success: false,
      email,
      reason: rawMessage || userFacingDetail,
    });
    throw new Error(
      JSON.stringify({
        detail: userFacingDetail,
        error: "NetworkError",
      }),
    );
  }

  const text = await response.text();
  let payload: IdentityTokenPayload;
  try {
    payload = text ? (JSON.parse(text) as IdentityTokenPayload) : {};
  } catch {
    payload = { detail: text || `${failureLabel} failed with ${response.status}` };
  }

  if (!response.ok) {
    if (response.status === 403 && payload.is_verified === false) {
      throw new Error(
        JSON.stringify({
          is_verified: false,
          email,
          detail: payload.detail || "Email verification required",
          status: response.status,
        }),
      );
    }
    await auditLogService.logAuthentication("login_failed", failureLabel, {
      success: false,
      email,
      reason: text || `${failureLabel} failed with ${response.status}`,
    });
    throw new Error(JSON.stringify(payload.detail ? payload : { detail: text || `${failureLabel} failed` }));
  }

  if (payload.status === "organization_selection_required") {
    // Hub never shows an org picker (facility-org plan / AB#10255). Identity
    // must auto-bind; treat residual challenges as a provisioning failure.
    throw new Error(
      "Account organization could not be resolved automatically. Contact support.",
    );
  }

  return persistIdentitySession(payload, email);
}

async function postOidcStart(body: Record<string, unknown>): Promise<{
  state: string;
  authorizationUrl: string;
}> {
  const identityOrigin = requireIdentityBaseUrl();
  const response = await fetch(`${identityOrigin}${OIDC_START_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || `SSO start failed with ${response.status}`);
  }
  const data = JSON.parse(text) as { state?: string; authorization_url?: string };
  if (!data.state || !data.authorization_url) {
    throw new Error("Invalid SSO start response.");
  }
  return { state: data.state, authorizationUrl: data.authorization_url };
}

export async function startOidcLogin(
  provider: OidcProvider,
): Promise<{ state: string; authorizationUrl: string }> {
  return postOidcStart({
    provider,
    redirect_uri: loginRedirectUri(),
    audience: IDENTITY_AUDIENCE,
    intent: "login",
  });
}

/**
 * Identity OIDC start with signup intent (MCM/identity contract).
 * `signupId` must match a pending MCM signup row (`signup_id_required`).
 */
export async function startOidcSignup(
  provider: OidcProvider,
  signupId: string,
): Promise<{ state: string; authorizationUrl: string }> {
  const id = String(signupId || "").trim();
  if (!id) {
    throw new Error("signup_id_required");
  }
  return postOidcStart({
    provider,
    redirect_uri: loginRedirectUri(),
    audience: IDENTITY_AUDIENCE,
    intent: "signup",
    signup_id: id,
  });
}

/**
 * Create MCM UniComply pending_oidc draft + identity authorize URL (AB#10394).
 * Prefer this from Hub `/signup` when no signup_id is in query/state yet.
 */
export async function startUnicomplyOidcSignup(
  provider: OidcProvider,
  options?: { displayName?: string; idempotencyKey?: string },
): Promise<{ signupId: string; state: string; authorizationUrl: string }> {
  const identityOrigin = requireIdentityBaseUrl();
  const idempotencyKey =
    String(options?.idempotencyKey || "").trim() ||
    `${Date.now()}-uc-${provider}-${Math.random().toString(36).slice(2, 10)}`;
  const displayName = String(options?.displayName || "").trim();
  const response = await fetch(
    `${identityOrigin}${UNICOMPLY_SIGNUP_OIDC_START_PATH}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        provider,
        idempotency_key: idempotencyKey,
        ...(displayName ? { display_name: displayName } : {}),
      }),
    },
  );
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || `UniComply SSO signup start failed with ${response.status}`);
  }
  let data: {
    signup_id?: string;
    state?: string;
    authorization_url?: string;
  };
  try {
    data = text ? (JSON.parse(text) as typeof data) : {};
  } catch {
    throw new Error("Invalid UniComply SSO signup start response.");
  }
  if (!data.signup_id || !data.state || !data.authorization_url) {
    throw new Error("Invalid UniComply SSO signup start response.");
  }
  return {
    signupId: String(data.signup_id),
    state: data.state,
    authorizationUrl: data.authorization_url,
  };
}

/** Begin browser redirect to the identity IdP (authorization-code + PKCE). */
export async function redirectToOidcLogin(provider: OidcProvider): Promise<void> {
  const { authorizationUrl } = await startOidcLogin(provider);
  window.location.assign(authorizationUrl);
}

function isUnicomplySignupDraftUnavailable(error: unknown): boolean {
  const message = (error instanceof Error ? error.message : String(error ?? "")).trim();
  if (!message) {
    return false;
  }
  if (/\b404\b/i.test(message) || /not found/i.test(message)) {
    return true;
  }
  try {
    const parsed = JSON.parse(message) as { detail?: unknown };
    return String(parsed.detail ?? "").toLowerCase() === "not found";
  } catch {
    return false;
  }
}

/**
 * Begin browser redirect for identity-owned social signup (intent=signup).
 * When `signupId` is omitted, creates a UniComply MCM draft first (AB#10394).
 * Falls back to login OIDC when the MCM draft route is unavailable (local identity-only dev).
 */
export async function redirectToOidcSignup(
  provider: OidcProvider,
  signupId?: string,
  options?: { displayName?: string },
): Promise<void> {
  const id = String(signupId || "").trim();
  if (id) {
    const { authorizationUrl } = await startOidcSignup(provider, id);
    window.location.assign(authorizationUrl);
    return;
  }
  try {
    const started = await startUnicomplyOidcSignup(provider, {
      displayName: options?.displayName,
    });
    window.location.assign(started.authorizationUrl);
  } catch (error) {
    if (!isUnicomplySignupDraftUnavailable(error)) {
      throw error;
    }
    const { authorizationUrl } = await startOidcLogin(provider);
    window.location.assign(authorizationUrl);
  }
}

const OIDC_CALLBACK_CLAIM_PREFIX = "hub_oidc_callback:";

export function getOidcCallbackClaimStatus(
  state: string,
): "processing" | "done" | null {
  const key = `${OIDC_CALLBACK_CLAIM_PREFIX}${state}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing === "processing" || existing === "done") {
      return existing;
    }
    return null;
  } catch {
    return null;
  }
}

/** Guard React StrictMode / remount double POST of the same OIDC callback. */
export function tryClaimOidcCallback(state: string): boolean {
  const key = `${OIDC_CALLBACK_CLAIM_PREFIX}${state}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing === "processing" || existing === "done") {
      return false;
    }
    sessionStorage.setItem(key, "processing");
    return true;
  } catch {
    return true;
  }
}

/** Wait for the owning StrictMode leg to finish persisting the identity session. */
export function waitForOidcCallbackDone(
  state: string,
  timeoutMs = 30000,
): Promise<boolean> {
  return new Promise((resolve) => {
    const started = Date.now();
    const poll = () => {
      if (
        getOidcCallbackClaimStatus(state) === "done" ||
        localStorage.getItem("access_token")
      ) {
        resolve(true);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(false);
        return;
      }
      window.setTimeout(poll, 100);
    };
    poll();
  });
}

export function stripOidcReturnParams(params: URLSearchParams): string {
  params.delete("code");
  params.delete("state");
  params.delete("iss");
  return params.toString();
}

export function releaseOidcCallbackClaim(state: string): void {
  try {
    sessionStorage.removeItem(`${OIDC_CALLBACK_CLAIM_PREFIX}${state}`);
  } catch {
    /* ignore */
  }
}

export function finalizeOidcCallbackClaim(state: string): void {
  try {
    sessionStorage.setItem(`${OIDC_CALLBACK_CLAIM_PREFIX}${state}`, "done");
  } catch {
    /* ignore */
  }
}

export async function completeOidcLogin(input: {
  state: string;
  code: string;
}): Promise<LoginResponse> {
  return postIdentityAuth(
    OIDC_CALLBACK_PATH,
    {
      state: input.state,
      code: input.code,
    },
    "",
    "Google",
  );
}

export async function loginWithEmailPassword(
  email: string,
  password: string
): Promise<LoginResponse> {
  requireIdentityBaseUrl();

  try {
    return await postIdentityAuth(
      LOGIN_PATH,
      {
        email,
        password,
        audience: IDENTITY_AUDIENCE,
      },
      email,
      "Email",
    );
  } catch (error) {
    if (error instanceof Error && !error.message.includes("Login failed")) {
      await auditLogService.logAuthentication("login_failed", "Email", {
        success: false,
        email,
        reason: error.message,
      });
    }
    throw error;
  }
}

type IdentityActionResponse = {
  status?: string;
  message?: string;
  detail?: string;
};

async function postAction(
  baseUrl: string,
  path: string,
  body: Record<string, unknown>,
  token?: string,
): Promise<IdentityActionResponse> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  let payload: IdentityActionResponse = {};
  try {
    payload = text ? (JSON.parse(text) as IdentityActionResponse) : {};
  } catch {
    payload = { detail: text };
  }
  if (!response.ok) {
    throw new Error(payload.detail || payload.message || text || `Auth request failed with ${response.status}`);
  }
  return payload;
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<IdentityActionResponse> {
  const token = localStorage.getItem("access_token");
  if (!token) {
    throw new Error("Not authenticated");
  }
  return postAction(
    requireRulesBaseUrl(),
    "/api/v1/auth/change-password",
    { current_password: currentPassword, new_password: newPassword },
    token,
  );
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
  status_code: number;
}

export async function resetPassword(
  email: string,
  newPassword: string,
): Promise<ResetPasswordResponse> {
  if (!RULES_BASE_URL) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }

  try {
    const response = await fetch(`${RULES_BASE_URL}${RESET_PASSWORD_PATH}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, new_password: newPassword }),
    });

    if (!response.ok) {
      const text = await response.text();
      await auditLogService.logAuthentication("password_reset", "Email", {
        success: false,
        email,
        reason: `Password reset failed: ${text || response.status}`,
      });
      throw new Error(text || `Password reset failed with ${response.status}`);
    }

    const data = (await response.json()) as ResetPasswordResponse;
    await auditLogService.logAuthentication("password_reset", "Email", {
      success: true,
      email,
    });
    return data;
  } catch (error) {
    if (
      error instanceof Error &&
      !error.message.includes("Password reset failed")
    ) {
      await auditLogService.logAuthentication("password_reset", "Email", {
        success: false,
        email,
        reason: error.message,
      });
    }
    throw error;
  }
}

export interface SignupPayload {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  display_name: string;
  provider_user_id?: string | null;
  is_verified?: boolean;
}

export async function signupWithEmail(payload: SignupPayload): Promise<LoginResponse> {
  if (!RULES_BASE_URL) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  try {
    const response = await fetch(`${RULES_BASE_URL}${REGISTER_PATH}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const text = await response.text();

      // Log failed signup attempt
      await auditLogService.logAuthentication('login_failed', 'Email', {
        success: false,
        email: payload.email,
        reason: `Signup failed: ${text || response.status}`,
      });

      throw new Error(text || `Signup failed with ${response.status}`);
    }

    const data = (await response.json()) as LoginResponse;
    
    console.log("🔍 [Auth] Signup response:", {
      hasAccessToken: !!data.access_token,
      tokenPreview: data.access_token ? data.access_token.substring(0, 20) + "..." : "NOT PROVIDED (email verification required)",
      expiresIn: data.expires_in,
      hasUser: !!data.user
    });
    
    // Store user data if available
    if (data.user) {
      localStorage.setItem("user", JSON.stringify(data.user));
    }
    
    // Only store access token if provided by backend
    // Backend may not provide token until email is verified
    if (data.access_token) {
      localStorage.setItem("access_token", data.access_token);
      invalidateMyFacilitiesCache();
      markUserActivity();
      
      // Store permissions if available - check both top level and user object
      let permissionsToStore: Permission[] | null = null;
      
      if (data.permissions && Array.isArray(data.permissions)) {
        permissionsToStore = data.permissions;
      } else {
        const userPermissions = (data.user as any)?.permissions;
        if (userPermissions && Array.isArray(userPermissions)) {
          permissionsToStore = userPermissions;
        }
      }
      
      if (permissionsToStore) {
        console.log("✅ [Auth] Storing permissions during signup:", permissionsToStore);
        localStorage.setItem("user_permissions", JSON.stringify(permissionsToStore));
      } else {
        console.warn("⚠️ [Auth] No permissions found in signup response. Checked:", {
          "data.permissions": data.permissions,
          "user.permissions": (data.user as any)?.permissions,
        });
      }

      // Calculate expiration - default to 1 hour if not provided
      const expiresInMs = data.expires_in ? data.expires_in * 1000 : 3600 * 1000;
      const expirationTime = Date.now() + expiresInMs;
      localStorage.setItem("access_token_expires_at", String(expirationTime));
      setSessionIdleTimeoutFromExpiresIn(data.expires_in ?? 3600);
      markUserActivity();
      
      console.log("✅ [Auth] Signup token stored:", {
        tokenStored: !!localStorage.getItem("access_token"),
        expirationStored: localStorage.getItem("access_token_expires_at"),
        expiresAt: new Date(expirationTime).toISOString()
      });
    } else {
      console.log("ℹ️ [Auth] No access token in signup response - email verification required before login");
    }

    // Refresh session ID after signup
    auditLogService.refreshSession();

    // Log successful signup
    await auditLogService.logAuthentication('signup', 'Email', {
      success: true,
      email: payload.email,
    });

    // Note: Permission assignment is now handled by the backend register API
    // No need to assign permissions from frontend

    return data;
  } catch (error) {
    // Ensure failed signup is logged
    if (error instanceof Error && !error.message.includes('Signup failed')) {
      await auditLogService.logAuthentication('login_failed', 'Email', {
        success: false,
        email: payload.email,
        reason: error.message,
      });
    }
    throw error;
  }
}


export function logoutLocal(): Promise<void> {
  // Capture data needed for audit logs before clearing storage
  const userId = parseInt(getCurrentUserId(), 10);
  const facility: any = getSelectedFacilityFromSession();

  // Clear all app data from storage immediately so the redirect isn't blocked
  try {
    localStorage.clear();
    sessionStorage.clear();
    invalidateMyFacilitiesCache();

    // Clear countries cache
    import("@utils/countryUtils").then(({ clearCountriesCache }) => {
      clearCountriesCache();
    });
  } catch (_) {
    // Fallback to removing known keys if clear fails
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    localStorage.removeItem("user_permissions");
    localStorage.removeItem("access_token_expires_at");
    localStorage.removeItem(ACCOUNT_STATE_STORAGE_KEY);
    clearSessionIdleTimeout();
    sessionStorage.removeItem("selected_plan");
    sessionStorage.removeItem("app_countries_data");
  }

  // Fire-and-forget audit logging — do not block the caller/redirect
  (async () => {
    try {
      await auditLogService.logAuthentication('logout', 'Email', {
        success: true,
      });

      // Also log via /api/v1/audit/log for centralized audit trail
      try {
        await auditLogService.logApiCallToAuditLog({
          user_id: userId,
          endpoint: '/logout',
          method: 'POST',
          request_payload: {},
          response_payload: {},
          status_code: 200,
          facility_info: facility
            ? {
                facility_id: facility.facility_id,
                facility_name: facility.facility_name,
              }
            : {},
          action_details: {
            page: 'Authentication / Session',
            action: 'LOGOUT',
          },
          action_type: 'LOGOUT',
          action_status: 'SUCCESS',
        });
      } catch (auditError) {
        console.error('Failed to log logout to /audit/log endpoint:', auditError);
      }

      await auditLogService.flush();
    } catch (error) {
      console.error('Failed to log logout action:', error);
    }
  })();

  return Promise.resolve();
}

export function isLocalAuthenticated(): boolean {
  const token = localStorage.getItem("access_token");
  if (!token) return false;
  if (isIdleTimedOut()) return false;
  return true;
}

export function ensureAuthOrLogout(): boolean {
  const ok = isLocalAuthenticated();
  if (!ok) {
    try {
      // Clear any stale UI/session state
      logoutLocal();
    } catch (_) {}
    
  }
  return ok;
}

export interface OTPSendResponse {
  success: boolean;
  message: string;
  data: {
    otp: string;
  };
  status_code: number;
}

export async function sendOTP(email: string): Promise<OTPSendResponse> {
  if (!RULES_BASE_URL) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  try {
    const response = await fetch(`${RULES_BASE_URL}${OTP_SEND_PATH}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const text = await response.text();

      // Log failed OTP send
      await auditLogService.logAuthentication('otp', 'OTP', {
        success: false,
        email,
        reason: `OTP send failed: ${text || response.status}`,
      });

      throw new Error(text || `OTP send failed with ${response.status}`);
    }

    const data = (await response.json()) as OTPSendResponse;

    // Log successful OTP send
    await auditLogService.logAuthentication('otp', 'OTP', {
      success: true,
      email,
    });

    return data;
  } catch (error) {
    // Ensure failed OTP send is logged
    if (error instanceof Error && !error.message.includes('OTP send failed')) {
      await auditLogService.logAuthentication('otp', 'OTP', {
        success: false,
        email,
        reason: error.message,
      });
    }
    throw error;
  }
}

export interface OTPVerifyResponse {
  success: boolean;
  message: string;
  data: {
    email_verified: boolean;
    access_token: string;
    expires_in: number;
    user: {
      is_verified?: boolean;
      permissions?: Permission[];
      [key: string]: unknown;
    };
    permissions?: Permission[];
  };
  status_code: number;
}

export async function verifyOTP(email: string, otp: string): Promise<OTPVerifyResponse> {
  if (!RULES_BASE_URL) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }
  
  try {
    const response = await fetch(`${RULES_BASE_URL}${OTP_VERIFY_PATH}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, otp }),
    });

    if (!response.ok) {
      const text = await response.text();
      
      // Log failed OTP verification
      await auditLogService.logAuthentication('otp', 'OTP', {
        success: false,
        email,
        reason: `OTP verification failed: ${text || response.status}`,
      });
      
      throw new Error(text || `OTP verification failed with ${response.status}`);
    }

    const data = (await response.json()) as OTPVerifyResponse;
    
    // Store access token from response
    localStorage.setItem("access_token", data.data.access_token);
    invalidateMyFacilitiesCache();
    const expirationTime = Date.now() + data.data.expires_in * 1000;
    localStorage.setItem("access_token_expires_at", String(expirationTime));
    setSessionIdleTimeoutFromExpiresIn(data.data.expires_in);
    markUserActivity();
    
    // Store user data from response
    localStorage.setItem("user", JSON.stringify(data.data.user));
    
    // Store permissions - check both data.permissions and data.user.permissions
    const permissions = data.data.permissions || data.data.user?.permissions;
    if (permissions && Array.isArray(permissions)) {
      localStorage.setItem("user_permissions", JSON.stringify(permissions));
    }
    
    // Log successful OTP verification
    await auditLogService.logAuthentication('otp', 'OTP', {
      success: true,
      email,
    });

    return data;
  } catch (error) {
    // Ensure failed OTP verification is logged
    if (error instanceof Error && !error.message.includes('OTP verification failed')) {
      await auditLogService.logAuthentication('otp', 'OTP', {
        success: false,
        email,
        reason: error.message,
      });
    }
    throw error;
  }
}

export interface SendWelcomeEmailResponse {
  success: boolean;
  message?: string;
  status_code?: number;
}

export async function sendWelcomeEmail(userId: number, email: string): Promise<SendWelcomeEmailResponse> {
  if (!RULES_BASE_URL) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  try {
    const response = await fetch(`${RULES_BASE_URL}/api/v1/email/send-welcome-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("access_token") || ""}`,
      },
      body: JSON.stringify({ user_id: userId, email }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Welcome email send failed with ${response.status}`);
    }

    const data = (await response.json()) as SendWelcomeEmailResponse;
    return data;
  } catch (error) {
    // Log error but don't throw - welcome email failure shouldn't block user flow
    console.error("Failed to send welcome email:", error);
    throw error;
  }
}

export async function resendOTP(email: string): Promise<OTPSendResponse> {
  if (!RULES_BASE_URL) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  try {
    // Use the same endpoint as sendOTP for resending
    const response = await fetch(`${RULES_BASE_URL}${OTP_SEND_PATH}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const text = await response.text();

      // Log failed OTP resend
      await auditLogService.logAuthentication('otp', 'OTP', {
        success: false,
        email,
        reason: `OTP resend failed: ${text || response.status}`,
      });

      throw new Error(text || `OTP resend failed with ${response.status}`);
    }

    const data = (await response.json()) as OTPSendResponse;

    // Log successful OTP resend
    await auditLogService.logAuthentication('otp', 'OTP', {
      success: true,
      email,
    });

    return data;
  } catch (error) {
    // Ensure failed OTP resend is logged
    if (error instanceof Error && !error.message.includes('OTP resend failed')) {
      await auditLogService.logAuthentication('otp', 'OTP', {
        success: false,
        email,
        reason: error.message,
      });
    }
    throw error;
  }
}

export interface UpdateProfilePayload {
  first_name?: string;
  last_name?: string;
  display_name?: string;
  bio?: string;
  timezone?: string;
  locale?: string;
  avatar_url?: string;
  phone_number?: string;
}

export interface UpdateProfileResponse {
  success: boolean;
  message?: string;
  data?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    display_name?: string;
    bio?: string;
    timezone?: string;
    locale?: string;
    avatar_url?: string;
    [key: string]: unknown;
  };
  status_code?: number;
}

export async function updateProfile(payload: UpdateProfilePayload): Promise<UpdateProfileResponse> {
  if (!RULES_BASE_URL) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  const response = await authenticatedFetch(`${RULES_BASE_URL}/api/v1/auth/me`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Profile update failed with ${response.status}`);
  }

  const data = (await response.json()) as UpdateProfileResponse;

  // If response doesn't have success field, assume success based on status code
  if (data.success === undefined) {
    data.success = true;
  }

  // Update localStorage user data if response includes updated user data
  // Handle both direct user object in response or nested in data field
  const userData = data.data || (data as any);
  const responsePhone =
    userData?.phone_number ??
    (typeof payload.phone_number === "string" ? payload.phone_number : undefined);

  if (
    userData &&
    (userData.first_name ||
      userData.last_name ||
      userData.email ||
      responsePhone != null)
  ) {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        const updatedUser = {
          ...user,
          first_name: userData.first_name ?? user.first_name,
          last_name: userData.last_name ?? user.last_name,
          display_name: userData.display_name ?? user.display_name,
          bio: userData.bio ?? user.bio,
          timezone: userData.timezone ?? user.timezone,
          locale: userData.locale ?? user.locale,
          avatar_url: userData.avatar_url ?? user.avatar_url,
          // Prefer API value, then the phone we just saved, then existing.
          phone_number: responsePhone ?? user.phone_number,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        try {
          if (updatedUser.email) {
            sessionStorage.setItem("UserEmail", updatedUser.email);
          }
          if (updatedUser.phone_number) {
            sessionStorage.setItem(
              "sessionPhoneNumber",
              String(updatedUser.phone_number),
            );
          }
        } catch (_) {}
        try {
          window.dispatchEvent(new CustomEvent("unicomply:user-updated", { detail: { user: updatedUser } }));
        } catch (_) {}
      } catch (error) {
        console.error("Failed to update localStorage user:", error);
      }
    }
  }

  return data;
}


